import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomTextInput from '@/components/basic/input-text/input-text.component';
import { Button, Spinner, Alert } from 'flowbite-react';
import { useFormik } from 'formik';
import { routeNames } from '../../configs/routePaths';
import { cpfMask, cellphoneMask, singleDigitMask } from '@/utils/masks';
import { useUserContext } from '@/contexts/user.context'

import { IBureauUser } from '../../interfaces/user'
import { useGetUserSanitization, useGetBureauUser, useGetChatBot } from '@/hooks/queries/use-get-user-data';
import {
  increaseTriesOfSanitization,
  updateRegisterData,
  updateContacts,
  validateMFA,
  sendCodeMFA,
  finalizeSanitization
} from '../../services/users';

import { LoadChat } from '@/utils/load-chat';
import Constants from '@/configs/constants';
import useLocalStorage from '@/hooks/common/use-local-storage';

const PassoModal = {
  ALERTA_1: 0,
  ALERTA_2: 1,
  DADOS_CADASTRAIS: 2,
  INFORMACOES_CONTATO: 3,
  VERIFICACAO_EMAIL: 4,
  VERIFICACAO_CELULAR: 5,
  SUCESSO: 6,
  FALHA: 7
};

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  const maskedLocal = local[0] + '*';

  const [domainName, domainExt] = domain.split('.');
  const maskedDomain = domainName[0] + '****';

  return `${maskedLocal}@${maskedDomain}.${domainExt}`;
}

function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');

  if (digits.length < 10) return 'Número inválido';

  const ddd = digits.slice(0, 2);
  const meio = digits.slice(2, -4).replace(/\d/g, '*');
  const fim = digits.slice(-4);

  return `(${ddd}) ${meio}-${fim}`;
}

const SanitizationContainer = () => {
  const [textoTituloModal, setTextoTituloModal] = useState('');
  const [textoSubtituloModal, setTextoSubtituloModal] = useState<JSX.Element | null>(null);
  const [tries, setTries] = useState<number | null>(null);
  const [passoModal, setPassoModal] = useState<number>(PassoModal.ALERTA_1);
  const [bureau, setBureau] = useState<IBureauUser>({} as IBureauUser);
  const [isLoadingGeneral, setIsLoadingGeneral] = useState(false);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [celular, setCelular] = useState("");
  const [email, setEmail] = useState("");
  const { data: dataChat } = useGetChatBot() || {};
  const [showAlert, setShowAlert] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [_, setForceSanitization] = useLocalStorage(Constants.FORCE_SANITIZATION, btoa('false'));

  const { logOut } = useUserContext()
  const navigate = useNavigate();
  const { data: sanitizationData, isLoading } = useGetUserSanitization();

  const formRef = useRef<HTMLFormElement>(null);

  const formikDadosCadastrais = useFormik({
    enableReinitialize: true,
    initialValues: {
      cpf: bureau.cpf ?? '',
      name: bureau.name ?? '',
      surname: bureau.surname ?? '',
      birthDate: bureau.birthDate
        ? new Date(bureau.birthDate).toISOString().split('T')[0]
        : ''
    },
    onSubmit: async values => {
      const payload = {
        cpf: values.cpf,
        name: values.name,
        surname: values.surname,
        birthDate: values.birthDate
      };

      setIsLoadingGeneral(true);

      try {
        await updateRegisterData(payload);
        setPassoModal(PassoModal.INFORMACOES_CONTATO);
      } catch (error: any) {
        console.error("Erro capturado:", error);

        if (error?.fieldErrors?.length) {
          error.fieldErrors.forEach((fieldError: any) => {
            console.error(`Erro no campo ${fieldError.field}: ${fieldError.errorMessage}`);

            formikDadosCadastrais.setFieldError(fieldError.field, fieldError.errorMessage);
            formikDadosCadastrais.setFieldTouched(fieldError.field, true, false);
          });
        }
      }

      setIsLoadingGeneral(false);
    }
  });

  const formikConfirmacaoContato = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: bureau.email ?? '',
      cellPhone: bureau.cellPhone ?? ''
    },
    onSubmit: async values => {

      const payload = {
        cellPhone: values.cellPhone,
        email: values.email
      };

      setCelular(values.cellPhone);
      setEmail(values.email);

      setIsLoadingGeneral(true);

      try {
        await updateContacts(payload);

        if (sanitizationData.emailMfaValidated || emailValidated)
          setPassoModal(PassoModal.VERIFICACAO_CELULAR)
        else
          setPassoModal(PassoModal.VERIFICACAO_EMAIL)

      } catch (error: any) {
        if (error?.fieldErrors?.length) {
          error.fieldErrors.forEach((fieldError: any) => {
            formikConfirmacaoContato.setFieldError(fieldError.field, fieldError.errorMessage);
            formikConfirmacaoContato.setFieldTouched(fieldError.field, true, false);
          });
        } else {
          setPassoModal(PassoModal.FALHA);
        }
      }

      setIsLoadingGeneral(false);
    }
  });

  const formikVerificacao = useFormik({
    enableReinitialize: true,
    initialValues: {
      digito1: '',
      digito2: '',
      digito3: '',
      digito4: '',
      digito5: '',
      digito6: ''
    },
    onSubmit: async (values, { resetForm }) => {

      const payload = {
        code: values.digito1 + values.digito2 + values.digito3 + values.digito4 + values.digito5 + values.digito6,
        optInCommunication: "EMAIL"
      };

      setIsLoadingGeneral(true);

      if (passoModal === PassoModal.VERIFICACAO_EMAIL) {

        if (sanitizationData.emailMfaValidated)
          setPassoModal(PassoModal.VERIFICACAO_CELULAR);

        try {
          await validateMFA(payload);
          resetForm();
          setPassoModal(PassoModal.VERIFICACAO_CELULAR);
          setEmailValidated(true);
        } catch (error: any) {

          if (error.errors?.length) {
            setGeneralErrors(error.errors);
          } else if (error.status === 500) {
            setPassoModal(PassoModal.FALHA);
          }
        }

      } else if (passoModal === PassoModal.VERIFICACAO_CELULAR) {

        try {
          payload.optInCommunication = "SMS";
          await validateMFA(payload);
          setPassoModal(PassoModal.SUCESSO);
        } catch (error: any) {
          if (error.errors?.length) {
            setGeneralErrors(error.errors);
          } else if (error.status === 500) {
            setPassoModal(PassoModal.FALHA);
          }
        }

      }

      setIsLoadingGeneral(false);
    }
  });

  const bureauQuery = useGetBureauUser();

  useEffect(() => {

    setGeneralErrors([]);

    if (passoModal === PassoModal.FALHA) {
      setTextoTituloModal('Não é possível atualizar seus dados!');
      setTextoSubtituloModal(
        <>
          <div className='md:col-span-3 col-span-12 flex mx-auto justify-center items-center mb-6'>
            <img
              src='/images/boneco_pensativo.svg'
              alt='Logo'
              width={160}
              title='Fale conosco'
            />
          </div>
          Tente novamente mais tarde.
        </>
      );
    } else if (passoModal === PassoModal.SUCESSO) {
      setTextoTituloModal('Atualizamos suas informações de acesso!');
      setTextoSubtituloModal(
        <>
          <div className='md:col-span-3 col-span-12 flex mx-auto justify-center items-center mb-6'>
            <img
              src='/images/joinha.svg'
              alt='Logo'
              width={160}
              title='Fale conosco'
            />
          </div>
          Para finalizar, você precisa entrar novamente no portal. <br />
          <span className='font-bold'>Importante:</span> se atualizou o e-mail, use o novo para se conectar.
        </>
      );
    } else if (passoModal === PassoModal.VERIFICACAO_CELULAR) {
      setTextoTituloModal('Verificação 2 de 2 - Insira o código de 6 números que enviamos para ' + maskPhone(celular));
      setTextoSubtituloModal(
        <>Enviamos um <span className='font-bold'>código de verificação</span> para confirmar que seus dados estão corretos.</>
      );
    } else if (passoModal === PassoModal.VERIFICACAO_EMAIL) {
      setTextoTituloModal("Verificação 1 de 2 - Insira o código de 6 números que enviamos para o " + maskEmail(email));
      setTextoSubtituloModal(
        <>
          Enviamos um <span className="font-bold">código de verificação</span> para confirmar que seus dados estão corretos.
          Verifique o e-mail de <span className="font-bold">não-responda@acessovr.com.br</span>.
        </>
      );
    } else if (passoModal === PassoModal.INFORMACOES_CONTATO) {

      const isBureauEmpty = Object.keys(bureau).length === 0

      if (isBureauEmpty) {
        setIsLoadingGeneral(true)

        bureauQuery.refetch().then(({ data: bureauData }) => {
          if (bureauData) {
            setBureau(bureauData);
          }
        }).finally(() => {
          setIsLoadingGeneral(false)
        })
      }

      setTextoTituloModal('Atualize também suas informações de contato');
      setTextoSubtituloModal(
        <>
          Informe um <span className='font-bold'>e-mail exclusivo</span>, pois o acesso ao portal é individual e fazemos verificações para garantir a segurança dos dados.
        </>
      );
    } else if (passoModal === PassoModal.DADOS_CADASTRAIS) {

      setIsLoadingGeneral(true);
      bureauQuery.refetch().then(({ data: bureauData }) => {
        if (bureauData) {
          setBureau(bureauData);
          setTextoTituloModal('Atualize seus dados cadastrais');
          setTextoSubtituloModal(
            <>Confirme os dados e/ou corrija o que for necessário para avançar.</>
          );

          formikDadosCadastrais.setFieldValue(
            'birthDate',
            new Date(bureauData.birthDate).toISOString().split('T')[0]
          )
        }
      })
        .finally(() => {
          setIsLoadingGeneral(false);
        });

    } else if (passoModal === PassoModal.ALERTA_2) {
      setPassoModal(PassoModal.ALERTA_2);
      setTextoTituloModal('Atualização obrigatória!');
      setTextoSubtituloModal(
        <>
          Por segurança, seu acesso ao portal está temporariamente bloqueado até que os dados cadastrais da sua empresa sejam atualizados.<br /><br />
          <span className='font-bold'>Siga para atualização agora e recupere seu acesso imediatamente.</span><br /><br />
          Caso tenha dificuldades com a atualização entre em contato com: <br />
          <span className='font-bold'>(11) 4004-4938</span> - Capital e regiões metropolitanas<br />
          <span className='font-bold'>0800 596 3838</span> - Demais localidades<br />
          <span className='font-bold'>ChatBot Portal VRM</span> 7. Falar com Especialista
        </>
      );
    } else if (passoModal === PassoModal.ALERTA_1) {
      setTextoTituloModal('Atualize seus dados para garantir mais segurança!');
      setTextoSubtituloModal(
        <>
          Manter as informações da sua empresa sempre atualizadas é essencial para proteger seus acessos e garantir uma experiência segura no portal.<br /><br />
          Reserve um minuto para conferir e atualizar seus dados cadastrais.
        </>
      );
    }

  }, [passoModal]);

  useEffect(() => {
    if (!sanitizationData) return;

    const {
      acessoVrIntegrationFailed,
      acessoVrIntegrationEnabled,
      acessoVrIntegrationCompleted,
      acessoVrIntegrationTries,
      emailMfaValidated,
      smsMfaValidated
    } = sanitizationData;

    if (!acessoVrIntegrationEnabled || acessoVrIntegrationCompleted || acessoVrIntegrationFailed) {
      setForceSanitization(btoa('false'));
      navigate(routeNames.dashboard);
      return;
    }

    setForceSanitization(btoa('true'));
    
    if (emailMfaValidated)
      setEmailValidated(true);

    const currentTries = Math.min((acessoVrIntegrationTries ?? 0) + 1, 3);
    setTries(currentTries);

    if(smsMfaValidated){
      setPassoModal(PassoModal.SUCESSO);
    } else if (currentTries > 1) {
      setPassoModal(PassoModal.ALERTA_2);
    } else {
      setPassoModal(PassoModal.ALERTA_1);
    }
  }, [sanitizationData]);

  useEffect(() => {
    if (!showAlert) return;

    const timer = setTimeout(() => {
      setShowAlert(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [showAlert]);

  const handleGoBackClick = () => {

    if (passoModal > 0) {

      if (passoModal == PassoModal.VERIFICACAO_CELULAR && (sanitizationData.emailMfaValidated || emailValidated))
        setPassoModal(passoModal - 2);
      else
        setPassoModal(passoModal - 1);
    }

  };

  useEffect(() => {
    LoadChat(dataChat);
  }, [dataChat]);

  const handleIrParaPortal = async () => {
    
    setIsLoadingGeneral(true);

    try {
      await finalizeSanitization();
      logOut();
    } catch (error: any) {
      if (error.errors?.length) {
        setGeneralErrors(error.errors);
      } else if (error.status === 500) {
        setPassoModal(PassoModal.FALHA);
      }
    }

    setIsLoadingGeneral(false);
  };

  const handleTryLaterClick = async () => {
    setForceSanitization(btoa('false'));
    setIsLoadingGeneral(true);
    await increaseTriesOfSanitization();
    setIsLoadingGeneral(false);
    navigate(routeNames.dashboard);
  };

  const handleUpdateClick = async () => {

    setIsLoadingGeneral(true);

    try {
      await increaseTriesOfSanitization();

      if (!sanitizationData.bureauInfoValidated) {
        setPassoModal(PassoModal.DADOS_CADASTRAIS);
      } else if (sanitizationData.bureauInfoValidated && (!sanitizationData.smsMfaValidated || !sanitizationData.emailMfaValidated)) {
        setPassoModal(PassoModal.INFORMACOES_CONTATO);
      }

    } catch (error: any) {
      setPassoModal(PassoModal.FALHA);
    }

    setIsLoadingGeneral(false);

  };

  const handleEnviarNovoCodigo = async () => {
    setIsLoadingGeneral(true);

    try {
      await sendCodeMFA();
      setShowAlert(true);
      formikVerificacao.resetForm();
    } catch (error: any) {
      if (error.errors?.length) {
        setGeneralErrors(error.errors);
      } else if (error.status === 500) {
        setPassoModal(PassoModal.FALHA);
      }
    }

    setIsLoadingGeneral(false);
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    formikDadosCadastrais.setFieldValue('cpf', cpfMask(target.value));
  };

  const handleCelularChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    formikConfirmacaoContato.setFieldValue('cellPhone', cellphoneMask(target.value));
  };

  const handleChangeDigitos = (e, index) => {

    const value = e.target.value.replace(/\D/g, '').slice(0, 1);
    formikVerificacao.setFieldValue(`digito${index + 1}`, value);

    if (value) {
      const nextInput = document.querySelector(`input[name="digito${index + 2}"]`) as HTMLInputElement
      nextInput?.focus()
    }
  };

  const handleKeyDownDigitos = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    const value = formikVerificacao.values[`digito${index + 1}`];
    if (e.key === 'Backspace' && !value && index > 0) {
      const prevInput = document.querySelector(`input[name="digito${index}"]`) as HTMLInputElement;
      prevInput?.focus();
    }
  };

  const renderEtapa = () => {
    switch (passoModal) {
      case PassoModal.ALERTA_1:
      case PassoModal.ALERTA_2:
        return (
          <div className='flex justify-between items-center flex-row mt-5 text-center'>
            {tries && tries < 2 && (
              <div
                className='text-green-500 hover:text-green-700 w-full md:w-11/12 pr-2'
                onClick={handleTryLaterClick}
              >
                <button>Lembrar depois</button>
                <div className='text-[10px] cursor-pointer'>
                  (Caso escolha essa opção, no próximo acesso, a atualização de seus dados será obrigatória)
                </div>
              </div>
            )}
            <Button
              type='button'
              color='success'
              className='bg-primary-dark hover:bg-primary-hover w-full'
              onClick={handleUpdateClick}
            >
              Atualizar dados
            </Button>
          </div>
        );

      case PassoModal.DADOS_CADASTRAIS:
        return (
          <form
            ref={formRef}
            onSubmit={formikDadosCadastrais.handleSubmit}
            className='flex flex-col md:grid grid-cols-12 w-full p-3 bg-white gap-5 mb-5 rounded-lg'
          >
            <div className='col-span-12'>
              <CustomTextInput
                type='text'
                field='cpf'
                formik={formikDadosCadastrais}
                onKeyUp={e => handleCpfChange(e as any)}
                label='CPF'
                required
              />
            </div>
            <div className='col-span-12'>
              <CustomTextInput type='text' field='name' formik={formikDadosCadastrais} label='Nome' required />
            </div>
            <div className='col-span-12'>
              <CustomTextInput type='text' field='surname' formik={formikDadosCadastrais} label='Sobrenome' required />
            </div>
            <div className='col-span-12'>
              <CustomTextInput type='date' field='birthDate' formik={formikDadosCadastrais} label='Data de nascimento:' required />
            </div>
            <div className='col-span-12'>
              <Button className='bg-primary-dark hover:bg-primary-hover w-full' type='submit' size='sm'>
                Avançar
              </Button>
            </div>
            <ProgressBar etapa={1} />
          </form>
        );

      case PassoModal.INFORMACOES_CONTATO:
        return (
          <form
            ref={formRef}
            onSubmit={formikConfirmacaoContato.handleSubmit}
            className='flex flex-col md:grid grid-cols-12 w-full p-3 bg-white gap-5 mb-5 rounded-lg'
          >
            <div className='col-span-12'>
              <CustomTextInput type='text' field='email' formik={formikConfirmacaoContato} label='E-mail' required disabled={emailValidated} />
              <label className='text-[10px] block'>Informe um e-mail que só você usa. Vamos precisar confirmar se este e-mail é seu e está correto.</label>
            </div>
            <div className='col-span-12'>
              <CustomTextInput
                type='text'
                field='cellPhone'
                formik={formikConfirmacaoContato}
                label='Celular'
                onKeyUp={e => handleCelularChange(e as any)}
                required />
              <label className='text-[10px] block'>Informe um celular que só você usa. Vamos precisar confirmar se este celular é seu e está correto.</label>
            </div>
            <div className='col-span-12'>
              <Button className='bg-primary-dark hover:bg-primary-hover w-full' type='submit' size='sm'>
                Avançar
              </Button>
            </div>
            <ProgressBar etapa={2} />
          </form>
        );

      case PassoModal.VERIFICACAO_EMAIL:
      case PassoModal.VERIFICACAO_CELULAR:
        return (
          <form
            ref={formRef}
            onSubmit={formikVerificacao.handleSubmit}
            className='flex flex-col md:grid grid-cols-12 w-full p-3 bg-white gap-5 mb-5 rounded-lg'
          >
            <div className='col-span-12 text-center font-bold'>Informe o código de verificação.</div>
            {[...Array(6)].map((_, i) => (
              <div key={i} className='col-span-2'>
                <CustomTextInput
                  type='text'
                  field={`digito${i + 1}`}
                  formik={formikVerificacao}
                  maxDigits={1}
                  mask={singleDigitMask}
                  onChange={(e) => handleChangeDigitos(e, i)}
                  onKeyDown={(e) => handleKeyDownDigitos(e, i)}
                  required
                />
              </div>
            ))}
            <div className='col-span-12'>
              <a
                href='#'
                className='text-[10px] block underline'
                onClick={(e) => {
                  e.preventDefault();
                  handleEnviarNovoCodigo();
                }}>Envie outro código
              </a>
              {showAlert && (
                <Alert color="success" className='mt-3'>
                  Código enviado com sucesso!
                </Alert>
              )}
            </div>
            <div className='col-span-12 text-center text-[10px]'>
              Não recebi o código. O que fazer?
            </div>
            <div className='col-span-12 bg-gray-200 p-4 text-[10px]'>
              {passoModal === PassoModal.VERIFICACAO_EMAIL && (
                <>
                  * Confira na sua caixa de spam (lixo eletrônico) ou promoções. <br />
                  * Caso não reconheça o seu e-mail, clique em voltar e atualize o e-mail digitado.
                </>
              )}
              {passoModal === PassoModal.VERIFICACAO_CELULAR && (
                <>* Caso não reconheça o seu celular, clique em voltar e atualize o número digitado.</>
              )}
              <br />
              * Entre em contato com a gente em um de nossos canais de atendimento:<br />
              <span className='ml-8 font-bold'>(11) 4004-4938</span> - Capital e regiões metropolitanas<br />
              <span className='ml-8 font-bold'>0800 596 3838</span> - Demais localidades<br />
              <span className='ml-8 font-bold'>ChatBot Portal VRM</span> - 7. Falar com Especialista
            </div>
            <div className='col-span-6'>
              <Button className='w-full' type='button' color='light' onClick={handleGoBackClick}>
                Voltar
              </Button>
            </div>
            <div className='col-span-6'>
              <Button className='bg-primary-dark hover:bg-primary-hover w-full' type='submit' size='sm'>
                Verificar
              </Button>
            </div>
            <ProgressBar etapa={passoModal === PassoModal.VERIFICACAO_EMAIL ? 3 : 4} />
          </form>
        );

      case PassoModal.SUCESSO:
        return (
          <div className='flex justify-between items-center flex-row mt-5 text-center'>
            <Button
              type='button'
              color='success'
              className='bg-primary-dark hover:bg-primary-hover w-full'
              onClick={handleIrParaPortal}>
              Ir para o Portal
            </Button>
          </div>
        );

      case PassoModal.FALHA:
        return (
          <div className='flex justify-between items-center flex-row mt-5 text-center'>
            <Button 
              type='button' 
              color='success' 
              className='bg-primary-dark hover:bg-primary-hover w-full'
              onClick={() => setPassoModal(PassoModal.ALERTA_2)}>
              OK
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const ProgressBar = ({ etapa }: { etapa: number }) => (
    <>
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className={`bg-quaternary col-span-3 h-[3px] ${i <= etapa ? '' : 'bg-opacity-20'}`}
        ></div>
      ))}
      <div className='col-span-12 text-center'>
        <label className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
          Etapa {etapa.toString().padStart(2, '0')} de 04
        </label>
      </div>
    </>
  );

  return (
    <main className='h-screen flex flex-1 flex-col'>
      <div className='flex md:flex-row flex-col-reverse justify-center items-center p-10 h-full z-10 md:w-9/12 mx-auto max-w-[650px]'>
        {isLoadingGeneral || isLoading ? (
          <Spinner data-testid="loading" color="success" aria-label="Carregando..." />
        ) : (
          <div className='flex rounded-lg border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-800 flex-col relative w-full'>
            <div className='bg-quaternary rounded'>
              <p className='font-semibold text-center text-white p-5'>
                {textoTituloModal}
              </p>
            </div>
            <div className='md:p-10 p-5'>
              <label className='block mb-2 text-sm font-medium text-gray-900 dark:text-white text-center'>
                {textoSubtituloModal}
              </label>

              {renderEtapa()}

              {generalErrors.length > 0 && (
                <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
                  <ul className="list-disc list-inside space-y-1">
                    {generalErrors.map((err, index) => (
                      <li key={index}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default SanitizationContainer;
